/**
 * Constants for the Reports page — icon maps, status chips, category tabs,
 * and the static fallback template list used when the API is unreachable.
 *
 * Extracted from Reports.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.2). Uses the .tsx extension because several maps embed JSX icons
 * from lucide-react.
 */

import {
  TrendingUp,
  FileText,
  BarChart3,
  ArrowLeftRight,
  ArrowDownCircle,
} from "lucide-react";
import type { ReportType, ReportTemplate } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Report-type icon + status maps
// ---------------------------------------------------------------------------

export const TYPE_ICONS: Record<ReportType, React.ReactNode> = {
  revenue: <TrendingUp className="h-5 w-5" />,
  tva: <FileText className="h-5 w-5" />,
  margin: <BarChart3 className="h-5 w-5" />,
  cashflow: <ArrowLeftRight className="h-5 w-5" />,
  expenses: <ArrowDownCircle className="h-5 w-5" />,
};

export const REPORT_STATUS_COLORS: Record<string, string> = {
  generating: "bg-blue-100 text-blue-800 border-blue-200",
  ready: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

export const REPORT_STATUS_LABELS: Record<string, string> = {
  generating: "En cours",
  ready: "Pret",
  failed: "Echec",
};

// ---------------------------------------------------------------------------
// Report category tabs
// ---------------------------------------------------------------------------

export type ReportCategory =
  | "ventes"
  | "produits"
  | "clients"
  | "livraisons"
  | "paiements";

export interface ReportCategoryTab {
  readonly id: ReportCategory;
  readonly label: string;
  readonly icon: React.ReactNode;
}

export const CATEGORY_TABS: readonly ReportCategoryTab[] = [
  { id: "ventes", label: "Ventes", icon: <TrendingUp className="h-4 w-4" /> },
  { id: "produits", label: "Produits", icon: <BarChart3 className="h-4 w-4" /> },
  { id: "clients", label: "Clients", icon: <FileText className="h-4 w-4" /> },
  { id: "livraisons", label: "Livraisons", icon: <ArrowLeftRight className="h-4 w-4" /> },
  { id: "paiements", label: "Paiements", icon: <ArrowDownCircle className="h-4 w-4" /> },
];

// ---------------------------------------------------------------------------
// Static fallback templates — mirrors the TEMPLATES constant in the backend
// route.ts. Shown when the API is unreachable so the page stays useful.
// Keep in sync with distribution-api/src/app/api/admin/finance/reports/route.ts
// ---------------------------------------------------------------------------

export const STATIC_FALLBACK_TEMPLATES: readonly ReportTemplate[] = [
  { id: "tpl-001", name: "Compte de Résultat", description: "État des revenus et dépenses par période", type: "revenue" },
  { id: "tpl-002", name: "Analyse des Marges", description: "Marges brutes et nettes par produit et catégorie", type: "margin" },
  { id: "tpl-003", name: "État de Trésorerie", description: "Flux de trésorerie entrants et sortants", type: "cashflow" },
  { id: "tpl-004", name: "Rapport des Dépenses", description: "Détail des dépenses par catégorie et période", type: "expenses" },
  { id: "tpl-005", name: "Rapport TVA", description: "Déclaration TVA mensuelle ou trimestrielle", type: "tva" },
];
